import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ConfirmDialog from './ConfirmDialog';

const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [formData, setFormData] = useState({
    filial_nr: '',
    name: '',
    address: ''
  });
  const [filialNrError, setFilialNrError] = useState('');

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/api/branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkFilialNrDuplicate = (value) => {
    if (!value) {
      setFilialNrError('');
      return;
    }
    
    const duplicate = branches.find(branch => 
      branch.filial_nr === value && 
      (!editingBranch || branch.id !== editingBranch.id)
    );
    
    if (duplicate) {
      setFilialNrError(`⚠️ Filialnummer existiert bereits bei "${duplicate.name}"`);
    } else {
      setFilialNrError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prüfe auf Duplikat vor dem Speichern
    if (filialNrError) {
      alert('Bitte beheben Sie die Fehler bevor Sie speichern.');
      return;
    }
    
    try {
      if (editingBranch) {
        await axios.put(`/api/branches/${editingBranch.id}`, formData);
      } else {
        await axios.post('/api/branches', formData);
      }
      fetchBranches();
      closeModal();
    } catch (error) {
      console.error('Error saving branch:', error);
      alert('Fehler beim Speichern des Standorts');
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      filial_nr: branch.filial_nr || '',
      name: branch.name,
      address: branch.address || ''
    });
    setFilialNrError('');
    setShowModal(true);
  };

  const handleDelete = (branch) => {
    setConfirmDialog({
      show: true,
      title: 'Standort löschen',
      message: `Möchten Sie "${branch.name}" wirklich löschen?`,
      onConfirm: async () => {
        try {
          await axios.delete(`/api/branches/${branch.id}`);
          fetchBranches();
          setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
        } catch (error) {
          console.error('Error deleting branch:', error);
          alert('Fehler beim Löschen. Standort ist möglicherweise noch in Verwendung.');
        }
      }
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBranch(null);
    setFormData({
      filial_nr: '',
      name: '',
      address: ''
    });
    setFilialNrError('');
  };

  const filteredBranches = branches.filter(branch => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      branch.name.toLowerCase().includes(searchLower) ||
      (branch.filial_nr && branch.filial_nr.toString().includes(searchLower)) ||
      (branch.address && branch.address.toLowerCase().includes(searchLower))
    );
  });

  if (loading) {
    return <div className="loading">Lade Standorte...</div>;
  }

  return (
    <>
      <style>
        {`
          @keyframes buttonShimmer {
            0% {
              transform: translateX(-100%) translateY(-100%) rotate(45deg);
            }
            100% {
              transform: translateX(100%) translateY(100%) rotate(45deg);
            }
          }

          .button-primary {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: white;
            border: 2px solid rgba(255,255,255,0.2);
            padding: 0.75rem 1.5rem;
            borderRadius: 12px;
            cursor: pointer;
            fontSize: 0.95rem;
            fontWeight: 600;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            boxShadow: 0 8px 16px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255,255,255,0.2);
            position: relative;
            overflow: hidden;
          }

          .button-primary::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.3);
            transform: translate(-50%, -50%);
            transition: width 0.6s, height 0.6s;
          }

          .button-primary:hover::before {
            width: 300px;
            height: 300px;
          }

          .button-primary:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            transform: translateY(-3px) scale(1.05);
            box-shadow: 0 15px 35px rgba(59, 130, 246, 0.5), 0 0 30px rgba(59, 130, 246, 0.3);
          }

          .button-primary:active {
            transform: translateY(-1px) scale(1.02);
          }

          .action-button {
            border: 2px solid rgba(255,255,255,0.3);
            padding: 0.5rem 1rem;
            border-radius: 10px;
            cursor: pointer;
            fontSize: 0.875rem;
            font-weight: 600;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .action-button::before {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: linear-gradient(45deg, transparent, rgba(255,255,255,0.3), transparent);
            animation: buttonShimmer 3s infinite;
          }

          .action-button:hover {
            transform: translateY(-3px) scale(1.05);
          }

          .action-button:active {
            transform: translateY(-1px) scale(1.02);
          }

          .action-button-blue {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            color: white;
            box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
          }

          .action-button-blue:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            box-shadow: 0 12px 30px rgba(59, 130, 246, 0.5), 0 0 25px rgba(59, 130, 246, 0.4);
          }

          .action-button-red {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
            box-shadow: 0 4px 10px rgba(239, 68, 68, 0.3);
            min-width: 40px;
          }

          .action-button-red:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            box-shadow: 0 12px 30px rgba(239, 68, 68, 0.5), 0 0 25px rgba(239, 68, 68, 0.4);
            transform: translateY(-3px) scale(1.1) rotate(5deg);
          }

          .cancel-button {
            background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
            color: #475569;
            border: 2px solid #e2e8f0;
            padding: 0.75rem 1.5rem;
            border-radius: 10px;
            cursor: pointer;
            font-weight: 600;
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
          }

          .cancel-button::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(0, 0, 0, 0.05);
            transform: translate(-50%, -50%);
            transition: width 0.5s, height 0.5s;
          }

          .cancel-button:hover::before {
            width: 200px;
            height: 200px;
          }

          .cancel-button:hover {
            background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
            transform: translateY(-2px) scale(1.03);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
          }

          .branch-card {
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .branch-card:hover {
            transform: translateY(-6px) scale(1.02);
            box-shadow: 0 16px 32px rgba(0, 0, 0, 0.15), 0 0 30px rgba(59, 130, 246, 0.15);
          }

          .branch-badge {
            transition: all 0.3s ease;
          }

          .branch-badge:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          }
        `}
      </style>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div>
              <h1 style={{ color: '#1e293b', fontSize: '2rem', marginBottom: '0.5rem' }}>
                Standorte
              </h1>
              <p style={{ color: '#64748b' }}>
                Verwalten Sie alle Filialen und Standorte
              </p>
            </div>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <span 
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: '#3b82f6',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'help',
                  userSelect: 'none',
                  marginTop: '0.5rem'
                }}
                title="💡 Filialnummern-System:
                
≤ 3000: Interne Kategorien
(z.B. 1000 = Allgemein, 2000 = Marketing)
Diese werden bei Protokollen mit Filialauswahl angezeigt.

> 3000: Echte Niederlassungen
(z.B. 3001 = Wien Mitte, 3022 = Linz)
Bei diesen wird die Filiale automatisch übernommen.

Beispiele:
• 1000 - Allgemein
• 2000 - IT/Technik
• 3001 - Wien Mitte
• 3022 - Linz Hauptplatz"
              >
                i
              </span>
            </div>
          </div>
          <button onClick={() => setShowModal(true)} className="button-primary">
            Neuer Standort
          </button>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <input
            type="text"
            placeholder="Suche nach Name, Filialnummer oder Adresse..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '1rem'
            }}
          />
          <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#64748b' }}>
            {filteredBranches.length} von {branches.length} Standorten
          </div>
        </div>

        {filteredBranches.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '3rem',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>
              {searchTerm ? 'Keine Standorte gefunden' : 'Noch keine Standorte vorhanden'}
            </h3>
            <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
              {searchTerm ? 'Versuchen Sie andere Suchbegriffe' : 'Erstellen Sie Ihren ersten Standort'}
            </p>
            {!searchTerm && (
              <button onClick={() => setShowModal(true)} className="button-primary">
                Ersten Standort erstellen
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth > 768 ? 'repeat(auto-fill, minmax(300px, 1fr))' : '1fr',
            gap: '1.5rem'
          }}>
            {filteredBranches.map(branch => (
              <div
                key={branch.id}
                className="branch-card"
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div style={{ marginBottom: '1rem' }}>
                  {branch.filial_nr && (
                    <div 
                      className="branch-badge"
                      style={{
                        display: 'inline-block',
                        background: parseInt(branch.filial_nr) <= 3000 ? '#fef3c7' : '#eff6ff',
                        color: parseInt(branch.filial_nr) <= 3000 ? '#92400e' : '#3b82f6',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem'
                      }}
                      title={parseInt(branch.filial_nr) <= 3000 ? 'Interne Kategorie' : 'Echte Niederlassung'}
                    >
                      #{branch.filial_nr} {parseInt(branch.filial_nr) <= 3000 ? '📋' : '🏢'}
                    </div>
                  )}
                  <h3 style={{
                    color: '#1e293b',
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    marginBottom: '0.5rem'
                  }}>
                    {branch.name}
                  </h3>
                  {branch.address && (
                    <p style={{
                      color: '#64748b',
                      fontSize: '0.875rem',
                      lineHeight: '1.5'
                    }}>
                      {branch.address}
                    </p>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  gap: '0.5rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <button
                    onClick={() => handleEdit(branch)}
                    className="action-button action-button-blue"
                    style={{ flex: 1 }}
                  >
                    Bearbeiten
                  </button>
                  <button
                    onClick={() => handleDelete(branch)}
                    className="action-button action-button-red"
                    title="Löschen"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}
            onClick={closeModal}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1.5rem',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <h2 style={{ color: '#1e293b', fontSize: '1.25rem' }}>
                  {editingBranch ? 'Standort bearbeiten' : 'Neuen Standort erstellen'}
                </h2>
                <button
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
                <div style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  fontSize: '0.875rem',
                  color: '#1e40af'
                }}>
                  <strong>💡 Filialnummern-System:</strong>
                  <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
                    <li><strong>≤ 3000:</strong> Interne Kategorien (z.B. 1000 = Allgemein)</li>
                    <li><strong>&gt; 3000:</strong> Echte Niederlassungen (z.B. 3001 = Wien Mitte)</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Filialnummer
                  </label>
                  <input
                    type="text"
                    value={formData.filial_nr}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, filial_nr: value });
                      checkFilialNrDuplicate(value);
                    }}
                    placeholder="z.B. 1000 (intern) oder 3022 (Niederlassung)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: filialNrError ? '2px solid #ef4444' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s'
                    }}
                  />
                  {filialNrError && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.75rem',
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      color: '#dc2626',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      {filialNrError}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="z.B. Allgemein oder Wien 10 / Favoriten"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    Adresse
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows="2"
                    placeholder="z.B. Quellenstraße 149, 1100 Wien"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  marginTop: '1.5rem'
                }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="cancel-button"
                  >
                    Abbrechen
                  </button>
                  <button 
                    type="submit" 
                    className="button-primary"
                    disabled={filialNrError}
                    style={{
                      opacity: filialNrError ? 0.5 : 1,
                      cursor: filialNrError ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {editingBranch ? 'Änderungen speichern' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmDialog
          show={confirmDialog.show}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null })}
        />
      </div>
    </>
  );
};

export default Branches;